/**
 * HUD widget registry: how builders plug new pieces into the layout regions without editing
 * hud.ts. Add a `HudWidgetDef` to HUD_WIDGETS (one line per widget); Hud creates it in its
 * region at construction, calls `update` every frame (inside perf label `hud.<id>`), forwards
 * combat events to `ingest`, and disposes it with the HUD.
 *
 * `replaces` turns off a legacy piece the widget supersedes (the old unit frames, action bar,
 * keystone panel…), so old and new never draw at the same time.
 *
 * Probe contract: whatever replaces 'actionBar' must keep the latency probe's selectors working
 * (game/session.ts): container `.actionbar`, one `.action` child per UI_BIND_KEYS entry in that
 * order, and on each slot the classes `pressed` / `casting` / `queued` and a `.action-gcd` layer
 * toggling `.on`; and must call into `flashActionBind` (actionBar.ts) for the keydown flash.
 * kit/IconSlot already emits the slot half of this. Whatever replaces 'playerCast' must keep a
 * `.player-cast` element that toggles `.active`.
 */
import type { SimEvent, UnitId } from '@keystone/sim';
import type { HudHandlers, HudUpdate } from './hud';
import type { HudLayout, HudRegionName, HudRegions } from './layout';
import { skillClusterWidget } from './cluster';
import { PANEL_WIDGETS } from './panels/index';
import { bossWarningWidget } from './bossWarning';
import { FIELD_WIDGETS } from './field/index'; // +field
import { PROF_WIDGETS } from './professions/index'; // +prof
import { RANGER_HUD_WIDGETS } from './rangerHud'; // +ranger-specs

/** Pieces of the pre-redo HUD that a widget can replace. */
export type LegacyPiece = 'partyFrames' | 'playerFrame' | 'targetFrame' | 'playerCast' | 'actionBar' | 'keystone' | 'meters';

export interface HudWidget {
  /** Every frame. Keep it allocation-free; use dom.ts write-if-changed helpers and the kit. */
  update(h: HudUpdate, realNow: number): void;
  /** Combat events of the latest sim steps (same stream as floating text). */
  ingest?(events: readonly SimEvent[], playerId: UnitId | null, now: number): void;
  dispose?(): void;
}

export interface HudWidgetContext {
  layout: HudLayout;
  regions: HudRegions;
  handlers: HudHandlers;
  /** The HUD root (for overlays that are not in a region, e.g. banners). */
  root: HTMLElement;
}

export interface HudWidgetDef {
  /** Stable id (perf label `hud.<id>`, data-widget attribute). */
  id: string;
  region: HudRegionName;
  /** Legacy pieces this widget supersedes (they are not built). */
  replaces?: readonly LegacyPiece[];
  /** Build the widget into `host` (a fresh element appended to the region, class `hk-w-<id>`). */
  create(host: HTMLElement, ctx: HudWidgetContext): HudWidget;
}

/** Builders: register widgets here, in the order they should appear within their region. */
export const HUD_WIDGETS: HudWidgetDef[] = [
  skillClusterWidget, // bottomCenter: crest + bars + skill rows + player cast + XP (cluster/)
  bossWarningWidget, // raid-warning banner for boss signature / deadly casts (bossWarning.ts)
  ...PANEL_WIDGETS, // currencies, menu icons, keystone tracker + meters, loot feed, key hints (panels/)
  ...FIELD_WIDGETS, // +field: XP bar, quest tracker, Auto, NPC prompt, level-up banner (field/)
  ...PROF_WIDGETS, // +prof: Professions panel, node tooltip, gather bar, tracking, the Auto setting (professions/)
  ...RANGER_HUD_WIDGETS, // +ranger-specs: the Deadeye's reload bar, the Beastbound's beast bar (rangerHud.ts)
];

/** Legacy pieces turned off by the registered widgets. */
export function replacedPieces(defs: readonly HudWidgetDef[]): ReadonlySet<LegacyPiece> {
  const out = new Set<LegacyPiece>();
  for (const d of defs) for (const p of d.replaces ?? []) out.add(p);
  return out;
}
